import { useMemo } from "react";
import {
	DayOfWeek,
	formatDayOfWeek
} from "client/DateAndTime/culture";
import {
	addDays,
	getStartOfIsoWeek,
	getStartOfWeek,
	getDayOfWeek,
	getDayOfMonth,
	getIsToday
} from "client/DateAndTime/dateTimeUtils";
import { TimeZone } from "client/DateAndTime/timezones";
import { never } from "client_legacy/ndaw/errors";
import { useToday } from "./useToday";
import { WeekMode } from "client/Misc/commonTypes";

export type WeekDay = {
	day: DayOfWeek;
	date: Date;
	name: string;
	isToday: boolean;
	dayInTheMonth: number;
};

export function useWeekDays(
	dateWithinWeek: Date,
	mode: WeekMode,
	timezone: TimeZone
): readonly WeekDay[] {
	const today: Date = useToday({ timezone });

	const startOfTheWeek = useMemo(
		(): Date => {
			switch (mode) {
				case "culture":
					return getStartOfWeek(
						dateWithinWeek,
						timezone
					);
				case "iso":
					return getStartOfIsoWeek(
						dateWithinWeek,
						timezone
					);
				default:
					never(mode);
			}
		},
		[
			dateWithinWeek,
			mode,
			timezone
		]
	);

	return useMemo(
		(): readonly WeekDay[] => {
			const result: WeekDay[] = [];
			for (let i = 0; i < 7; i ++) {
				const currentDay: Date = addDays(
					startOfTheWeek,
					i,
					timezone
				);

				const day: DayOfWeek = getDayOfWeek(currentDay, timezone);
				const dayInTheMonth: number = getDayOfMonth(currentDay, timezone);

				result.push({
					day,
					date: currentDay,
					name: formatDayOfWeek(day, "short"),
					isToday: getIsToday(currentDay, timezone, today),
					dayInTheMonth
				});
			}
			return result;
		},
		[
			startOfTheWeek,
			timezone,
			today
		]
	);
}
